import React, {useCallback, useContext, useLayoutEffect, useMemo, useRef, useState} from "react";
import useResizeObserver from "../../hooks/use-resize-observer";
import useCombinedRefs from "../../hooks/use-combined-refs";
import classnames from "classnames";
import {DataGridMiscContext, DataGridScrollContext, DataGridStateContext, DEFAULT_DATA_GRID_LAYOUT_MAX_HEIGHT, DEFAULT_DATA_GRID_MIN_ROW_HEIGHT,} from "../../../index";
import DataGridBody from "../body";
import DataGridHeader from "../header";
import ObserversService from "../../../../../core/services/observers";
import debounce from "lodash/debounce";
import useIsMounted from "../../hooks/use-is-mounted";
import InfiniteScroll from "react-infinite-scroller";
import {resolveDataGridVirtualization, shouldVirtualizeDataGridRows} from "../../../core/services/utils/virtualization";

function virtualizationFallbackToCss(value) {
    if (value == null || value === '') {
        return undefined;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
        return `${value}px`;
    }
    return String(value);
}

/**
 * Maximum number of body rows to use for layout height (caps at actual row count).
 * @param {number | undefined} maxVisibleRows
 * @param {number} rowCount
 * @return {number}
 */
function getEffectiveVisibleRowCount(maxVisibleRows, rowCount) {
    const max = Math.max(0, maxVisibleRows ?? 0);
    const count = Math.max(0, rowCount ?? 0);
    return Math.min(max, count);
}

/**
 * Measures average row height from mounted body rows without coupling to layout container height.
 * @param {HTMLElement | null | undefined} bodyElement
 * @param {number} maxVisibleRows
 * @param {number} minRowHeight
 * @return {number}
 */
function measureAvgRowHeight(bodyElement, maxVisibleRows, minRowHeight) {
    const rowElements = Array.from(bodyElement?.querySelectorAll('.data-grid-body-row:not([data-grid-height-probe-row])') ?? []);
    const effectiveCount = getEffectiveVisibleRowCount(maxVisibleRows, rowElements.length);
    const measured = rowElements
        .slice(0, effectiveCount)
        .map(e => Math.max(minRowHeight, e.getBoundingClientRect().height ?? 0));
    if (!measured.length) {
        return minRowHeight;
    }
    const avg = measured.reduce((agg, c) => agg + c, 0) / measured.length;
    return Math.max(minRowHeight, avg);
}

/**
 * @param {DataGridRow[]} rows      - the rows to be displayed in the data-grid
 * @param {DataGridEvents} events    - the event handlers for the data-grid
 */
const DataGridTable = ({rows, events}) => {
    const {
        classNames,
        visibleRows,
        useInfiniteScroll,
        infiniteScrollHasMoreData,
        infiniteScrollGetScrollParent,
        virtualizationHeightFallback,
        minRowHeight: minRowHeightProp,
        ...virtualizationInput
    } = useContext(DataGridMiscContext);

    const {rowsEnabled, columnsEnabled} = resolveDataGridVirtualization(virtualizationInput);
    const useScrollport = rowsEnabled || columnsEnabled;

    const middleRowCount = useMemo(() => {
        if (!rowsEnabled) {
            return rows?.length ?? 0;
        }
        return rows?.filter(r => !r?.stickToTop && !r?.stickToBottom)?.length ?? 0;
    }, [rows, rowsEnabled]);

    const shouldVirtualizeRows = shouldVirtualizeDataGridRows(rowsEnabled, middleRowCount, visibleRows);

    const minRowHeight = minRowHeightProp ?? DEFAULT_DATA_GRID_MIN_ROW_HEIGHT;
    const {loading: {state: loading}, pagination, density} = useContext(DataGridStateContext);
    const [scrolling, setScrolling] = useState(false);
    const isMounted = useIsMounted();

    /**@type {React.MutableRefObject<HTMLDivElement>}*/
    const containerRef = useRef();
    const {rect: layoutRect, ref: layoutResizeObserverRef} = useResizeObserver();
    const ref = useCombinedRefs(containerRef, layoutResizeObserverRef);
    const mousePosition = useRef(null);
    const showEmptyContent = useMemo(() => !rows?.length, [rows?.length])

    const avgRowHeightRef = useRef(minRowHeight);
    const [virtualizedAvgRowHeight, setVirtualizedAvgRowHeight] = useState(minRowHeight);
    const syncContextRef = useRef({
        shouldVirtualizeRows,
        useScrollport,
        visibleRows,
        loading,
        showEmptyContent,
        minRowHeight,
    });
    syncContextRef.current = {
        shouldVirtualizeRows,
        useScrollport,
        visibleRows,
        loading,
        showEmptyContent,
        minRowHeight,
    };

    const getLayoutScrollParent = useCallback(() => containerRef.current ?? null, []);

    const scrollContextValue = useMemo(() => ({getLayoutScrollParent}), [getLayoutScrollParent]);

    const layoutMaxHeightStyle = useMemo(() => {
        if (!useScrollport) {
            return undefined;
        }
        const fallback = virtualizationHeightFallback ?? DEFAULT_DATA_GRID_LAYOUT_MAX_HEIGHT;
        return virtualizationFallbackToCss(fallback);
    }, [useScrollport, virtualizationHeightFallback]);

    /**
     * Row-virtualized grids: cap layout height to ~visibleRows (max) using a stable row estimate.
     * Does not read tbody ResizeObserver (avoids spacer feedback loops).
     * When virtualizationHeightFallback is set, maxHeight on the layout acts as a ceiling.
     */
    useLayoutEffect(() => {
        const layout = containerRef.current;
        if (!layout || !shouldVirtualizeRows) {
            return;
        }

        const headerElement = layout.querySelector('.data-grid-header');
        const headerHeight = headerElement?.getBoundingClientRect()?.height ?? 0;
        const rowHeightEstimate = virtualizedAvgRowHeight;
        const effectiveVisibleRows = getEffectiveVisibleRowCount(visibleRows, rows?.length ?? 0);
        const targetBodyHeight = effectiveVisibleRows * rowHeightEstimate;
        const newHeight = `${headerHeight + targetBodyHeight + 8}px`;

        if (layout.style.height !== newHeight) {
            layout.style.height = newHeight;
        }
    }, [
        shouldVirtualizeRows,
        visibleRows,
        rows?.length,
        virtualizedAvgRowHeight,
        layoutRect?.width,
    ]);

    useLayoutEffect(() => {
        if (shouldVirtualizeRows) {
            return;
        }
        avgRowHeightRef.current = minRowHeight;
        setVirtualizedAvgRowHeight(minRowHeight);
    }, [shouldVirtualizeRows, minRowHeight]);

    /**
     * Syncs layout height for non-row-virtualized grids via header/body ResizeObserver.
     */
    const syncHeight = useCallback((entries, observer) => {
        window.requestAnimationFrame(() => {
            const ctx = syncContextRef.current;
            if (!entries?.length || !containerRef.current || !isMounted() || ctx.shouldVirtualizeRows) {
                return;
            }

            if (!ctx.useScrollport && observer) {
                ObserversService.disconnectResizeObserver(observer);
            }

            let loadingHeight = containerRef.current?.querySelector('.data-grid-loading-container')?.getBoundingClientRect()?.height ?? 0
            let emptyContentHeight = containerRef.current?.querySelector('.data-grid-empty-content-container')?.getBoundingClientRect()?.height ?? 0;
            let headerHeight = 0;
            let bodyHeight = 0;
            const headerElement = containerRef.current?.querySelector('.data-grid-header')
            const bodyElement = containerRef.current?.querySelector('.data-grid-body')

            for (const entry of entries) {
                switch (entry?.target ?? entry) {
                    case headerElement:
                        headerHeight = entry?.contentRect?.height ?? entry?.getBoundingClientRect()?.height ?? 0;
                        break;
                    case bodyElement:
                        bodyHeight = entry?.contentRect?.height ?? entry?.getBoundingClientRect()?.height ?? 0;
                        break;
                    default:
                        break
                }
            }

            if (headerHeight === 0)
                headerHeight = headerElement?.getBoundingClientRect()?.height ?? 0;
            if (bodyHeight === 0)
                bodyHeight = bodyElement?.getBoundingClientRect()?.height ?? 0;

            const bodyRowElements = Array.from(bodyElement.querySelectorAll('.data-grid-body-row') ?? []);
            const effectiveVisibleRows = getEffectiveVisibleRowCount(ctx.visibleRows, bodyRowElements.length);
            const visibleRowsHeight = bodyRowElements
                .slice(0, effectiveVisibleRows)
                .map(e => e.getBoundingClientRect().height ?? 0)
                .reduce((agg, c) => agg + c, 0);

            const includeMinimumHeight = ctx.loading || ctx.showEmptyContent;

            const newHeight = Math.max(
                Math.max(
                    loadingHeight,
                    emptyContentHeight,
                    Math.min(
                        bodyHeight,
                        visibleRowsHeight
                    )
                ) + headerHeight + 8,
                includeMinimumHeight ? 400 + headerHeight + 8 : headerHeight + 8
            ) + 'px';

            if (containerRef.current.style.height !== newHeight) {
                containerRef.current.style.height = newHeight;
            }

            if (!ctx.useScrollport && observer) {
                ObserversService.observeResizeObserver(observer, headerElement);
                ObserversService.observeResizeObserver(observer, bodyElement);
            }
        })
    }, [isMounted]);

    /**
     * With each change in the [loading] state, or [showEmptyContent] value or the [visibleRows] value or [pagination] value:
     * - attaches an observer that would sync the height of the layout rect with the provided constraints.
     */
    useLayoutEffect(() => {
        if (shouldVirtualizeRows || !containerRef.current)
            return;

        const headerElement = containerRef.current?.querySelector('.data-grid-header')
        const bodyElement = containerRef.current?.querySelector('.data-grid-body')

        if (!headerElement || !bodyElement)
            return;

        const throttledSyncHeight = debounce(
            (entries, observer) => syncHeight(entries, observer),
            useScrollport ? 0 : 100,
        );
        const observer = ObserversService.newResizeObserver(throttledSyncHeight);
        ObserversService.observeResizeObserver(observer, headerElement);
        ObserversService.observeResizeObserver(observer, bodyElement);
        throttledSyncHeight([headerElement, bodyElement], observer)
        return () => ObserversService.disconnectResizeObserver(observer);
    }, [
        containerRef,
        pagination?.currentPage,
        pagination?.pageSize,
        visibleRows,
        loading,
        showEmptyContent,
        rows?.length,
        useScrollport,
        shouldVirtualizeRows,
        virtualizationHeightFallback,
        syncHeight,
    ])

    /**
     * Starts the scrolling of the data-grid content.
     * @param {MouseEvent | TouchEvent} e
     */
    const startScrolling = (e) => {
        const containerElement = e.target.closest('.data-grid-layout');
        if (containerElement !== containerRef.current) {
            return;
        }
        setScrolling(true);
        mousePosition.current = {
            pageX: e.pageX,
            pageY: e.pageY,
        }
    }

    /**
     * Scrolls the data-grid content with the given mouse movement.
     * @param {MouseEvent | TouchEvent} e
     */
    const scroll = useCallback((e) => {
        const layoutElement = containerRef.current;
        if (!layoutElement || !mousePosition.current) {
            return;
        }
        const movementX = (mousePosition.current.pageX - e.pageX);
        const movementY = (mousePosition.current.pageY - e.pageY);
        layoutElement.scrollBy({
            left: movementX,
            top: movementY,
            behavior: 'instant'
        })
        mousePosition.current = {
            pageX: e.pageX,
            pageY: e.pageY,
        }
    }, [])

    /**
     * Ends the scrolling of the data-grid content.
     */
    const endScrolling = useCallback(() => {
        setScrolling(false);
        mousePosition.current = null;
    }, [])

    useLayoutEffect(() => {
        if (scrolling) {
            document.documentElement.addEventListener('mousemove', scroll);
            document.documentElement.addEventListener('touchmove', scroll);
            document.documentElement.addEventListener('mouseup', endScrolling);
            document.documentElement.addEventListener('touchend', endScrolling);
        } else {
            document.documentElement.removeEventListener('mouseup', endScrolling);
            document.documentElement.removeEventListener('touchend', endScrolling);
        }
    }, [scrolling, scroll, endScrolling])

    useLayoutEffect(() => () => {
        document.documentElement.removeEventListener('mousemove', scroll);
        document.documentElement.removeEventListener('touchmove', scroll);
        document.documentElement.removeEventListener('mouseup', endScrolling);
        document.documentElement.removeEventListener('touchend', endScrolling);
    }, [scroll, endScrolling])

    /**
     * Loads more data into the data-grid.
     */
    const loadMore = useCallback(() => {
        events.onCurrentPageChanged?.(undefined, undefined, pagination?.currentPage + 1);
    }, [pagination?.currentPage, events.onCurrentPageChanged])

    const header = useMemo(() =>
            <DataGridHeader
                layoutRectWidth={layoutRect?.width}
            />,
        [layoutRect?.width])

    /**
     * Updates row height estimate for the virtualizer without coupling to layout container height changes.
     * Column virtualization measures a partial row, so that average comes from locked cell heights instead.
     */
    useLayoutEffect(() => {
        if (!shouldVirtualizeRows || columnsEnabled) {
            return;
        }
        const bodyElement = containerRef.current?.querySelector('.data-grid-body');
        const next = Math.round(measureAvgRowHeight(bodyElement, visibleRows, minRowHeight));
        if (Math.abs(next - avgRowHeightRef.current) >= 2) {
            avgRowHeightRef.current = next;
            setVirtualizedAvgRowHeight(next);
        }
    }, [shouldVirtualizeRows, columnsEnabled, rows, visibleRows, minRowHeight, loading, showEmptyContent, layoutRect?.width]);

    /**
     * Column virtualization replaces the DOM average, so a density change starts again from the row floor
     * until the cell probe publishes a new sample.
     */
    useLayoutEffect(() => {
        if (!shouldVirtualizeRows || !columnsEnabled) {
            return;
        }
        avgRowHeightRef.current = minRowHeight;
        setVirtualizedAvgRowHeight(minRowHeight);
    }, [columnsEnabled, density, minRowHeight, shouldVirtualizeRows]);

    /**
     * Applies the average locked row height once offscreen columns have been measured.
     * @type {(function(number): void)}
     */
    const onVirtualRowHeightSample = useCallback((avg) => {
        if (!syncContextRef.current.shouldVirtualizeRows) {
            return;
        }
        const next = Math.round(avg);
        if (Math.abs(next - avgRowHeightRef.current) >= 2) {
            avgRowHeightRef.current = next;
            setVirtualizedAvgRowHeight(next);
        }
    }, []);

    const avgRowHeight = useMemo(() => {
        if (!shouldVirtualizeRows) {
            const bodyElement = containerRef.current?.querySelector('.data-grid-body');
            return measureAvgRowHeight(bodyElement, visibleRows, minRowHeight);
        }
        return virtualizedAvgRowHeight;
    }, [shouldVirtualizeRows, rows, visibleRows, minRowHeight, loading, showEmptyContent, layoutRect?.width, virtualizedAvgRowHeight]);

    const body = useMemo(() =>
            <DataGridBody
                rows={rows}
                startScrolling={startScrolling}
                layoutRectWidth={layoutRect?.width}
                avgRowHeight={avgRowHeight}
                minRowHeight={minRowHeight}
                onVirtualRowHeightSample={onVirtualRowHeightSample}
            />,
        [rows, layoutRect?.width, avgRowHeight, minRowHeight, onVirtualRowHeightSample])

    const tableElement = useMemo(() => (
        <table
            className={classnames(
                'data-grid-table',
                classNames.table,
                {'scrolling': scrolling},
            )}
        >
            {header}
            {body}
        </table>
    ), [classNames.table, scrolling, header, body]);

    return (
        <>
            <div
                className={classnames(
                    'data-grid-layout',
                    useScrollport && 'data-grid-layout--scrollport',
                    classNames.layout,
                )}
                ref={ref}
                style={layoutMaxHeightStyle ? {maxHeight: layoutMaxHeightStyle} : undefined}
            >
                <DataGridScrollContext.Provider value={scrollContextValue}>
                    {
                        useInfiniteScroll &&
                        !shouldVirtualizeRows &&
                        <InfiniteScroll
                            pageStart={0}
                            loadMore={loadMore}
                            hasMore={infiniteScrollHasMoreData}
                            useWindow={false}
                            getScrollParent={infiniteScrollGetScrollParent ?? getLayoutScrollParent}
                            threshold={100}
                        >
                            {tableElement}
                        </InfiniteScroll>
                    }
                    {
                        (!useInfiniteScroll || shouldVirtualizeRows) &&
                        tableElement
                    }
                </DataGridScrollContext.Provider>
            </div>
        </>
    )
}

export default DataGridTable;
